// #region imports

// --- Angular
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { Constants, GroupModel, RespondentStatuses } from '../../../models';

// #endregion

@Component({
  selector: 'hs-add-edit-group-modal-v2',
  templateUrl: './add-edit-group-modal-v2.component.html',
  styleUrls: ['./add-edit-group-modal-v2.component.scss']
})
export class AddEditGroupModalV2Component implements OnChanges {

  @Input() group: GroupModel | null = null;
  @Input() groups: GroupModel[] | null = null;
  @Input() editMode: string = Constants.EDIT_MODE_NONE;
  @Input() isInvalidGroupName: boolean  = false;

  @Output() backButtonClicked = new EventEmitter();
  @Output() deleteGroup = new EventEmitter<GroupModel>();
  @Output() groupAdded = new EventEmitter<GroupModel>();
  @Output() groupEdited = new EventEmitter<string>();

  canAdd: boolean = false;
  canDelete: boolean = false;
  charsRemaining: number = 0;
  deleteAlert: string = '';
  errorMessage: string = '';
  groupName: string = '';
  groupNameChanged: boolean = false;
  groupNameError: string = '';
  isGroupNameEmpty: boolean = true;
  isGroupNameValid: boolean = false;

  modeEdit: string = Constants.EDIT_MODE_EDIT;

  maximumNumberOfGroupNameCharacters: number = Constants.GROUP_NAME_MAXIMUM_LENGTH;

  constructor(
    private _translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['group']) {
      if (this.group === null) return;

      this.groupName = this.group.groupName;
      this.isGroupNameEmpty = this.group.groupName.length === 0;
      this.charsRemaining = Constants.GROUP_NAME_MAXIMUM_LENGTH - this.groupName.length;
      this.isGroupNameValid = !this.isGroupNameEmpty && this.groupName.length <= Constants.GROUP_NAME_MAXIMUM_LENGTH;      
      this.canDelete = !(this.group.respondents.some(r => (r.status >= RespondentStatuses.added)));
    }
  }

  onBackButtonClicked(): void {
    this.backButtonClicked.emit();
  }

  onGroupNameChanged(event: Event) {
    if (this.group === null) return;

    this.deleteAlert = '';

    const target = event.target as HTMLInputElement;
    const newValue = (target?.value) ?? '';
    if (this.group.groupName !== newValue) this.groupNameChanged = true;

    if (newValue.trim().length === 0) {
      this.groupName = newValue.trim();
    } else {
      this.groupName = newValue;
    }

    if (this.groupName.length > Constants.GROUP_NAME_MAXIMUM_LENGTH) {
      const maxGroupName = this.groupName.substring(0, Constants.GROUP_NAME_MAXIMUM_LENGTH);
      this.groupName = maxGroupName;
      target.value = this.groupName;
    }
    this.isGroupNameEmpty = this.groupName.length === 0;
    this.charsRemaining = Constants.GROUP_NAME_MAXIMUM_LENGTH - this.groupName.length;

    this.isGroupNameValid = !this.isGroupNameEmpty && this.groupName.length <= Constants.GROUP_NAME_MAXIMUM_LENGTH;
  }

  onDelete(): void {
    if (!this.group) return;
    if (!this.groups) return;

    if (!this.canDelete) {
      this.deleteAlert = this._translateService.instant('v2ManageRespondentsDeleteGroupErrorRespondentsStarted');
      return;
    }

    this.deleteGroup.emit(this.group);
  }

  onSubmit(): void {
    if (!this.group) return;
    if (!this.groups) return;

    const duplicateGroupName = this.groups.find((g: GroupModel) => {
      if (g === null) return false;

      return !!(g.groupName.toLocaleLowerCase() === this.groupName.trim().toLocaleLowerCase()) && (g.index !== this.group?.index);
    });

    if (!!duplicateGroupName) {
      this.isInvalidGroupName = true;
      return;
    }

    if (this.editMode === Constants.EDIT_MODE_ADD) {
      this.group.groupName = this.groupName;
      this.groupAdded.emit(this.group);
    }

    if (this.editMode === Constants.EDIT_MODE_EDIT) {
      this.groupEdited.emit(this.groupName.trim());
    }

    this.onBackButtonClicked();
  }
}
